import { useEffect, useId, useState } from "react";
import {
  CustomHttpContractError,
  parseCustomHttpManifest,
  type CustomHttpAction,
  type CustomHttpConnection,
  type CustomHttpHeaderMapping,
  type CustomHttpManifest,
} from "@mph/custom-http";

import {
  accountErrorMessage,
  activateProjectCustomHttp,
  disableProjectCustomHttpConnection,
  getProjectCustomHttpSettings,
  saveProjectCustomHttpCredential,
  saveProjectCustomHttpManifest,
  type ProjectCustomHttpConnection,
  type ProjectCustomHttpSettings,
} from "../../lib/account-client";

export interface ProjectApiBuilderApi {
  getProjectCustomHttpSettings: typeof getProjectCustomHttpSettings;
  saveProjectCustomHttpManifest: typeof saveProjectCustomHttpManifest;
  saveProjectCustomHttpCredential: typeof saveProjectCustomHttpCredential;
  activateProjectCustomHttp: typeof activateProjectCustomHttp;
  disableProjectCustomHttpConnection: typeof disableProjectCustomHttpConnection;
}

export const projectApiBuilderApi: ProjectApiBuilderApi = {
  getProjectCustomHttpSettings,
  saveProjectCustomHttpManifest,
  saveProjectCustomHttpCredential,
  activateProjectCustomHttp,
  disableProjectCustomHttpConnection,
};

export function ProjectApiBuilder({
  workspaceId,
  projectId,
  expectedRevisionId,
  expectedAgentOperationId,
  onActivated,
  api = projectApiBuilderApi,
}: {
  workspaceId: string;
  projectId: string;
  expectedRevisionId?: string;
  expectedAgentOperationId?: string;
  onActivated?: () => void;
  api?: ProjectApiBuilderApi;
}) {
  const errorId = useId();
  const [settings, setSettings] = useState<ProjectCustomHttpSettings | null>(null);
  const [manifestJson, setManifestJson] = useState("");
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { getProjectCustomHttpSettings } = api;

  useEffect(() => {
    let active = true;
    setSettings(null);
    setError(null);
    void getProjectCustomHttpSettings(workspaceId, projectId)
      .then((result) => {
        if (!active) return;
        setSettings(result);
        setManifestJson(result.manifest ? JSON.stringify(result.manifest, null, 2) : "");
      })
      .catch((cause: unknown) => {
        if (active) setError(accountErrorMessage(cause, "Doorway could not load this project's API setup."));
      });
    return () => { active = false; };
  }, [getProjectCustomHttpSettings, loadAttempt, projectId, workspaceId]);

  const replaceSettings = (result: ProjectCustomHttpSettings, includeManifest = false) => {
    setSettings(result);
    setConfirmed(false);
    if (includeManifest && result.manifest) setManifestJson(JSON.stringify(result.manifest, null, 2));
  };

  const importManifest = async () => {
    if (!settings?.canManage || pending) return;
    setError(null);
    let parsed: CustomHttpManifest;
    try {
      parsed = parseCustomHttpManifest(JSON.parse(manifestJson));
    } catch (cause) {
      setError(cause instanceof CustomHttpContractError
        ? cause.message
        : "Manifest JSON must be valid before Doorway can review it.");
      return;
    }
    setPending("manifest");
    setAnnouncement("Reviewing the imported API action set.");
    try {
      const result = await api.saveProjectCustomHttpManifest(workspaceId, projectId, parsed);
      replaceSettings(result, true);
      setAnnouncement("API action set imported and ready for credential review.");
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not import this API action set."));
      setAnnouncement("Manifest import failed. Review the error and try again.");
    } finally {
      setPending(null);
    }
  };

  const exportManifest = () => {
    if (!settings?.manifest) return;
    const blob = new Blob([`${JSON.stringify(settings.manifest, null, 2)}\n`], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "doorway.api.json";
    link.click();
    URL.revokeObjectURL(url);
    setAnnouncement("Portable API manifest exported.");
  };

  const saveCredential = async (connection: ProjectCustomHttpConnection) => {
    if (!settings?.canManage || pending) return;
    const credential = credentials[connection.alias]?.trim();
    if (!credential) return;
    setPending(`credential:${connection.alias}`);
    setError(null);
    setAnnouncement(`${connection.status === "missing" ? "Saving" : "Rotating"} ${connection.alias} credential.`);
    try {
      const result = await api.saveProjectCustomHttpCredential(workspaceId, projectId, {
        alias: connection.alias,
        credential,
        expectedGeneration: connection.generation,
      });
      replaceSettings(result);
      setAnnouncement(`${connection.alias} credential saved. Its value will not be shown again.`);
    } catch (cause) {
      setError(accountErrorMessage(cause, `Doorway could not save the ${connection.alias} credential.`));
      setAnnouncement("Credential update failed. The entered value was cleared.");
    } finally {
      setCredentials((current) => ({ ...current, [connection.alias]: "" }));
      setPending(null);
    }
  };

  const disableConnection = async (connection: ProjectCustomHttpConnection) => {
    if (!settings?.canManage || pending || connection.status !== "active") return;
    setPending(`disable:${connection.alias}`);
    setError(null);
    setAnnouncement(`Disabling ${connection.alias}.`);
    try {
      replaceSettings(await api.disableProjectCustomHttpConnection(workspaceId, projectId, {
        alias: connection.alias,
        expectedGeneration: connection.generation,
      }));
      setAnnouncement(`${connection.alias} disabled. New API actions using it are blocked.`);
    } catch (cause) {
      setError(accountErrorMessage(cause, `Doorway could not disable ${connection.alias}.`));
      setAnnouncement("Connection disable failed.");
    } finally {
      setPending(null);
    }
  };

  const activate = async () => {
    if (!settings?.canManage || !settings.revisionId || !settings.manifestDigest || !confirmed || pending) return;
    setPending("activate");
    setError(null);
    setAnnouncement("Activating the reviewed revision and API action set.");
    try {
      const result = await api.activateProjectCustomHttp(workspaceId, projectId, {
        revisionId: settings.revisionId,
        manifestDigest: settings.manifestDigest,
        ...(expectedAgentOperationId ? { agentOperationId: expectedAgentOperationId } : {}),
      });
      replaceSettings(result);
      setAnnouncement("The exact revision and API action set are active.");
      if (result.status === "active") onActivated?.();
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not activate this API action set."));
      setAnnouncement("Activation failed. The prior live revision remains active.");
    } finally {
      setPending(null);
    }
  };

  if (!settings && !error) return <p className="workspace-empty" role="status">Loading custom API setup...</p>;
  if (!settings) return (
    <div className="review-stack">
      <p className="error-message" role="alert">{error}</p>
      <button className="secondary-button" type="button" onClick={() => {
        setError(null);
        setLoadAttempt((value) => value + 1);
      }}>Retry loading connections</button>
    </div>
  );
  if (!settings.eligible) {
    return <p className="workspace-tool-lock">Custom APIs are unavailable for this project.</p>;
  }
  if (expectedRevisionId && settings.revisionId !== expectedRevisionId) {
    return (
      <div className="review-stack">
        <p className="error-message" role="alert">Doorway returned a different API revision. Reload the exact agent review before approving anything.</p>
      </div>
    );
  }

  const connections = settings.connections;
  const manifest = settings.manifest;
  const readyToActivate = Boolean(
    settings.canManage
    && manifest
    && settings.revisionId
    && settings.manifestDigest
    && connections.length > 0
    && connections
      .filter((connection) => manifest.connections.some((required) => required.key === connection.alias))
      .every((connection) => connection.status === "active"),
  );

  return (
    <div className="project-api-builder">
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
      <div className="provider-connections-heading">
        <div>
          <p className="step-label">Custom APIs</p>
          <h3>API action set</h3>
          <p>Review the exact destinations and methods this site can use. Credentials stay outside the published files.</p>
        </div>
        {manifest ? <button className="text-button" type="button" onClick={exportManifest}>Export manifest</button> : null}
      </div>

      {!settings.canManage ? <p className="workspace-tool-lock">You can review this action set, but only a project manager can change it.</p> : null}
      {error ? <p className="error-message" id={errorId} role="alert">{error}</p> : null}
      {settings.validationErrors?.length ? (
        <ul className="provider-impact-list" aria-label="Manifest validation errors">
          {settings.validationErrors.map((entry) => <li key={`${entry.path}:${entry.message}`}><strong>{entry.path}</strong><span>{entry.message}</span></li>)}
        </ul>
      ) : null}

      {settings.canManage ? (
        <section aria-labelledby={`manifest-${projectId}`}>
          <h4 id={`manifest-${projectId}`}>Import a portable manifest</h4>
          <label>
            <span>Manifest JSON</span>
            <textarea
              rows={8}
              spellCheck={false}
              value={manifestJson}
              aria-describedby={error ? errorId : undefined}
              onChange={(event) => setManifestJson(event.target.value)}
              disabled={pending !== null}
            />
          </label>
          <button className="text-button" type="button" disabled={pending !== null || manifestJson.trim().length === 0} onClick={() => void importManifest()}>
            {pending === "manifest" ? "Reviewing..." : "Import manifest"}
          </button>
        </section>
      ) : null}

      {manifest ? (
        <>
          <section aria-labelledby={`connections-${projectId}`}>
            <h4 id={`connections-${projectId}`}>Connections</h4>
            <div className="provider-connection-list">
              {connections.map((connection) => (
                <fieldset key={connection.alias} aria-label={`${connection.alias} connection`}>
                  <legend>{connectionLabel(connection.alias)}</legend>
                  <p><strong>{connectionStatusLabel(connection.status)}</strong>{connection.fingerprint ? <> <span>{connection.fingerprint}</span></> : null}</p>
                  {connection.externallyManaged ? (
                    <p>This credential is provided by the local Worker environment.</p>
                  ) : settings.canManage ? (
                    <>
                      <label>
                        <span>Credential</span>
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={credentials[connection.alias] ?? ""}
                          onChange={(event) => setCredentials((current) => ({ ...current, [connection.alias]: event.target.value }))}
                          disabled={pending !== null}
                        />
                      </label>
                      <div className="provider-connection-actions">
                        <button className="text-button" type="button" disabled={pending !== null || !(credentials[connection.alias]?.trim())} onClick={() => void saveCredential(connection)}>
                          {connection.status === "missing" ? "Save credential" : "Rotate credential"}
                        </button>
                        {connection.status === "active" ? (
                          <button className="text-button danger-button" type="button" disabled={pending !== null} onClick={() => void disableConnection(connection)}>
                            Disable {connection.alias}
                          </button>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </fieldset>
              ))}
            </div>
          </section>

          <section aria-labelledby={`actions-${projectId}`}>
            <h4 id={`actions-${projectId}`}>Reviewed actions</h4>
            <div className="workspace-domain-sites">
              {manifest.actions.map((action) => (
                <article key={action.key} className="workspace-domain-site workspace-domain-site-open">
                  <div className="workspace-domain-panel">
                    <div className="provider-connection-summary">
                      <span className={`provider-status provider-status-${action.risk === "read" ? "ready" : "paused"}`}>{riskLabel(action.risk)}</span>
                      <strong>{action.key}</strong>
                      <small>{action.method}</small>
                    </div>
                    <p><code>{action.origin}{action.path}</code></p>
                    <pre aria-label={`${action.key} redacted request recipe`}>{requestRecipe(action, manifest.connections)}</pre>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="workspace-delete-zone" aria-label="Activate API action set">
            <div>
              <strong>{settings.status === "active" ? "Action set active" : "Activate this exact revision"}</strong>
              <span>Static files and API actions activate together after every credential is ready.</span>
            </div>
            {settings.canManage && settings.revisionId && settings.manifestDigest ? (
              <div>
                <label>
                  <input type="checkbox" checked={confirmed} disabled={!readyToActivate || pending !== null} onChange={(event) => setConfirmed(event.target.checked)} />
                  <span>{`Confirm revision ${settings.revisionId} with digest ${settings.manifestDigest}`}</span>
                </label>
                <button className="primary-button" type="button" disabled={!readyToActivate || !confirmed || pending !== null} onClick={() => void activate()}>
                  {pending === "activate" ? "Activating..." : "Activate exact revision"}
                </button>
              </div>
            ) : null}
          </section>
        </>
      ) : <p className="workspace-empty">Import a secret-free <code>doorway.api.json</code> to start.</p>}
    </div>
  );
}

function connectionLabel(alias: string): string {
  return alias.replace(/[-_.]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function connectionStatusLabel(status: ProjectCustomHttpConnection["status"]): string {
  if (status === "active") return "Ready";
  if (status === "disabled") return "Disabled";
  return "Credential needed";
}

function riskLabel(risk: CustomHttpAction["risk"]): string {
  return risk === "read" ? "Read" : "Mutation";
}

function requestRecipe(action: CustomHttpAction, connections: CustomHttpConnection[]): string {
  const lines = [`${action.method} ${action.origin}${action.path}`];
  for (const query of action.query ?? []) lines.push(`Query ${query.name}: {${query.input}}`);
  for (const header of action.headers ?? []) lines.push(headerRecipe(header));
  const connection = connections.find((candidate) => candidate.key === action.connectionKey);
  if (connection) lines.push(credentialRecipe(connection));
  if (action.body?.mode === "json") lines.push(`JSON ${JSON.stringify(action.body.template, null, 2)}`);
  if (action.body?.mode === "text") lines.push(`Text body: {${action.body.input}}`);
  lines.push(`Response: ${action.response.mode}`);
  return lines.join("\n");
}

function headerRecipe(header: CustomHttpHeaderMapping): string {
  return "input" in header ? `Header ${header.name}: {${header.input}}` : `Header ${header.name}: ${header.value}`;
}

function credentialRecipe(connection: CustomHttpConnection): string {
  const credential = connection.credential;
  if (credential.type === "bearer") return "Header Authorization: Bearer [credential]";
  if (credential.type === "basic") return "Header Authorization: Basic [credential]";
  if (credential.type === "header") return `Header ${credential.name}: [credential]`;
  if (credential.type === "query") return `Query ${credential.name}: [credential]`;
  return `Webhook path at ${credential.expectedOrigin}: [credential]`;
}
